import React, { useState } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { Mail } from "lucide-react";

/**
 * InviteClientModal (SOUND-293)
 * -------------------------------------------------------
 * Extracted from the retired PartnerDashboard. Sends a
 * company-type client invitation bound to the caller's
 * partner company.
 */
export default function InviteClientModal({ onClose, onInvited }) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleInvite = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const body = {
        email: email.trim(),
        type: "company",
        companyType: "client",
      };
      if (companyName.trim()) body.companyName = companyName.trim();

      await makeApiRequest(
        "post",
        apiRoutes.createInvitation,
        { id: user.id, accessToken: user.accessToken },
        user.companyId,
        body
      );

      toast({ status: "success", title: "Invitation sent" });
      setEmail("");
      setCompanyName("");
      if (onInvited) onInvited();
      onClose();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to send invitation",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-md shadow-lg">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Invite Client</h3>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">
              Email
            </label>
            <input
              type="email"
              className="input input-bordered w-full"
              placeholder="client@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">
              Company Name (optional)
            </label>
            <input
              type="text"
              className="input input-bordered w-full"
              placeholder="e.g. Acme Corp"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              className="btn btn-ghost flex-1"
              onClick={() => {
                setEmail("");
                setCompanyName("");
                onClose();
              }}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary flex-1"
              onClick={handleInvite}
              disabled={submitting || !email.trim()}
            >
              <Mail className="h-4 w-4" />
              {submitting ? "Sending..." : "Send Invitation"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
